/**
 * VIQ API Service
 *
 * All VIQ endpoint calls + SSE streaming helper.
 * VIQ endpoints are at {VIXIO_BACKEND}/api/viq/* (not under product vertical).
 */

/**
 * Create a VIQ API service instance bound to the Nuxt context.
 * Usage in store actions: const api = createViqApi(this)
 * Usage in components: const api = createViqApi(this)
 */
export function createViqApi(ctx) {
  // In Vuex actions, `this` has $axios and $config directly.
  // In components, they are also available directly.
  const $axios = ctx.$axios;
  const baseURL = ctx.$config
    ? ctx.$config.baseURL
    : process.env.BASE_URL || "http://pc.loc:8005";

  const apiBase = `${baseURL}/api/viq`;

  function headers() {
    return { "Content-Type": "application/json" };
  }

  function jsonParams(extra = {}) {
    return { ...extra };
  }

  return {
    // ─── Conversations ─────────────────────────────────────────
    getConversations(params = {}) {
      return $axios({ method: "GET", url: `${apiBase}/conversations`, params: jsonParams(params), headers: headers() })
        .then((r) => r.data);
    },

    getConversation(id) {
      return $axios({ method: "GET", url: `${apiBase}/conversations/${id}`, params: jsonParams(), headers: headers() })
        .then((r) => r.data);
    },

    createConversation(data) {
      return $axios({ method: "POST", url: `${apiBase}/conversations`, params: jsonParams(), data, headers: headers() })
        .then((r) => r.data);
    },

    updateConversation(id, data) {
      return $axios({ method: "PATCH", url: `${apiBase}/conversations/${id}`, params: jsonParams(), data, headers: headers() })
        .then((r) => r.data);
    },

    deleteConversation(id) {
      return $axios({ method: "DELETE", url: `${apiBase}/conversations/${id}`, params: jsonParams(), headers: headers() })
        .then((r) => r.data);
    },

    // ─── Ask (Q&A) ──────────────────────────────────────────────
    ask(data) {
      return $axios({ method: "POST", url: `${apiBase}/ask`, params: jsonParams(), data, headers: headers() })
        .then((r) => r.data);
    },

    // ─── Reports ────────────────────────────────────────────────
    reportBrief(data) {
      return $axios({ method: "POST", url: `${apiBase}/report/brief`, params: jsonParams(), data, headers: headers() })
        .then((r) => r.data);
    },

    reportRefine(data) {
      return $axios({ method: "POST", url: `${apiBase}/report/refine`, params: jsonParams(), data, headers: headers() })
        .then((r) => r.data);
    },

    // ─── Lenses ─────────────────────────────────────────────────
    getLenses() {
      return $axios({ method: "GET", url: `${apiBase}/lenses`, params: jsonParams(), headers: headers() })
        .then((r) => r.data);
    },

    createLens(data) {
      return $axios({ method: "POST", url: `${apiBase}/lenses`, params: jsonParams(), data, headers: headers() })
        .then((r) => r.data);
    },

    deleteLens(id) {
      return $axios({ method: "DELETE", url: `${apiBase}/lenses/${id}`, params: jsonParams(), headers: headers() })
        .then((r) => r.data);
    },

    // ─── Artifacts ──────────────────────────────────────────────
    getArtifacts(params = {}) {
      return $axios({ method: "GET", url: `${apiBase}/artifacts`, params: jsonParams(params), headers: headers() })
        .then((r) => r.data);
    },

    createArtifact(data) {
      return $axios({ method: "POST", url: `${apiBase}/artifacts`, params: jsonParams(), data, headers: headers() })
        .then((r) => r.data);
    },

    updateArtifact(slug, data) {
      return $axios({ method: "PATCH", url: `${apiBase}/artifacts/${slug}`, params: jsonParams(), data, headers: headers() })
        .then((r) => r.data);
    },

    deleteArtifact(slug) {
      return $axios({ method: "DELETE", url: `${apiBase}/artifacts/${slug}`, params: jsonParams(), headers: headers() })
        .then((r) => r.data);
    },

    getPublicArtifact(slug) {
      return $axios({ method: "GET", url: `${apiBase}/artifacts/${slug}/public`, params: jsonParams() })
        .then((r) => r.data);
    },

    // ─── Action Items ───────────────────────────────────────────
    createActionItems(data) {
      return $axios({ method: "POST", url: `${apiBase}/action-items`, params: jsonParams(), data, headers: headers() })
        .then((r) => r.data);
    },

    extractActions(data) {
      return $axios({ method: "POST", url: `${apiBase}/extract-actions`, params: jsonParams(), data, headers: headers() })
        .then((r) => r.data);
    },

    submitFeedback(data) {
      return $axios({ method: "POST", url: `${apiBase}/feedback`, params: jsonParams(), data, headers: headers() })
        .then((r) => r.data);
    },

    // ─── Expansion ──────────────────────────────────────────────
    expansionBaseline(data) {
      return $axios({ method: "POST", url: `${apiBase}/expansion-baseline`, params: jsonParams(), data, headers: headers() })
        .then((r) => r.data);
    },

    // ─── Articles ───────────────────────────────────────────────
    getArticle(id) {
      return $axios({ method: "GET", url: `${apiBase}/articles/${id}`, params: jsonParams(), headers: headers() })
        .then((r) => r.data);
    },

    // ─── Upsell Interest ──────────────────────────────────────
    recordUpsellInterest(type, value, context) {
      return $axios({ method: "POST", url: `${apiBase}/upsell-interest`, data: { type, value, context }, headers: headers() })
        .then((r) => r.data);
    },

    // ─── SSE Streaming ─────────────────────────────────────────
    /**
     * Stream an SSE endpoint using fetch + ReadableStream.
     *
     * @param {string} endpoint - Relative path e.g. '/ask/stream'
     * @param {object} data - POST body
     * @param {object} callbacks - { onMeta, onText, onDone, onError }
     * @param {string} token - Bearer token
     * @param {object} [options] - { onRefreshToken: async () => newToken }
     * @returns {object} { abort } - Call abort() to cancel the stream
     */
    stream(endpoint, data, callbacks, token, options = {}) {
      const controller = new AbortController();
      const url = `${apiBase}${endpoint}`;

      function startStream(bearerToken) {
        fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
          },
          body: JSON.stringify(data),
          credentials: "include",
          signal: controller.signal,
        })
          .then((response) => {
            if (!response.ok) {
              // On 401, try refreshing the token once and retry
              if (response.status === 401 && options.onRefreshToken && !options._retried) {
                options._retried = true;
                options.onRefreshToken()
                  .then((newToken) => {
                    if (newToken) {
                      startStream(newToken);
                    } else if (callbacks.onError) {
                      callbacks.onError(new Error("Session expired. Please refresh the page."));
                    }
                  })
                  .catch(() => {
                    if (callbacks.onError) {
                      callbacks.onError(new Error("Session expired. Please refresh the page."));
                    }
                  });
                return;
              }
              if (callbacks.onError) {
                callbacks.onError(new Error(`HTTP ${response.status}: ${response.statusText}`));
              }
              return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            function processChunk() {
              reader
                .read()
                .then(({ done, value }) => {
                  if (done) {
                    // Process any remaining buffer
                    if (buffer.trim()) {
                      parseSSEBuffer(buffer, callbacks);
                    }
                    // Don't call onDone here — the SSE "done" event handles it.
                    // This just means the HTTP connection closed.
                    return;
                  }

                  buffer += decoder.decode(value, { stream: true });

                  // Split on double newlines (SSE event boundary)
                  const parts = buffer.split("\n\n");
                  // Keep the last incomplete part in buffer
                  buffer = parts.pop() || "";

                  parts.forEach((part) => {
                    if (part.trim()) {
                      parseSSEEvent(part.trim(), callbacks);
                    }
                  });

                  processChunk();
                })
                .catch((err) => {
                  if (err.name !== "AbortError" && callbacks.onError) {
                    callbacks.onError(err);
                  }
                });
            }

            processChunk();
          })
          .catch((err) => {
            if (err.name !== "AbortError" && callbacks.onError) {
              callbacks.onError(err);
            }
          });
      }

      startStream(token);

      return { abort: () => controller.abort() };
    },
  };
}

/**
 * Parse a single SSE event block.
 * Format:
 *   event: meta|text|done
 *   data: {"key": "value"}
 */
function parseSSEEvent(block, callbacks) {
  let eventType = "message";
  const dataLines = [];

  block.split("\n").forEach((line) => {
    if (line.startsWith("event:")) {
      eventType = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    }
  });

  const rawData = dataLines.join("\n");
  let parsed = rawData;

  try {
    parsed = JSON.parse(rawData);
  } catch {
    // Keep as string if not valid JSON
  }

  switch (eventType) {
    case "meta":
      if (callbacks.onMeta) callbacks.onMeta(parsed);
      break;
    case "text":
      if (callbacks.onText) callbacks.onText(parsed);
      break;
    case "done":
      if (callbacks.onDone) callbacks.onDone(parsed);
      break;
    case "error":
      if (callbacks.onError) callbacks.onError(parsed);
      break;
    case "status":
      if (callbacks.onStatus) callbacks.onStatus(parsed);
      break;
    case "config":
      if (callbacks.onConfig) callbacks.onConfig(parsed);
      break;
    default:
      break;
  }
}

function parseSSEBuffer(buffer, callbacks) {
  buffer.split("\n\n").forEach((block) => {
    if (block.trim()) {
      parseSSEEvent(block.trim(), callbacks);
    }
  });
}
